import React, { useState, useEffect, useCallback } from 'react';
import { Search, Download, MapPin, Filter, Calendar } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminHistory() {
  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userF, setUserF] = useState('');
  const [fromF, setFromF] = useState('');
  const [toF, setToF] = useState('');
  const [page, setPage] = useState(1);
  const PER = 50;

  const load = useCallback(async (pg=1) => {
    setLoading(true);
    try {
      const [histR, usersR] = await Promise.all([
        api.get('/admin/search-history', { params:{ userId:userF, from:fromF, to:toF, page:pg, limit:PER } }),
        api.get('/admin/users', { params:{ limit:200 } })
      ]);
      setHistory(histR.data.history||[]); setTotal(histR.data.total||0); setPage(pg);
      setUsers(usersR.data.users||[]);
    } catch { toast.error('Failed to load history'); }
    finally { setLoading(false); }
  }, [userF, fromF, toF]);

  useEffect(()=>{ load(1); }, [userF,fromF,toF]);

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams(); if (userF) params.set('userId',userF);
      const r = await api.get(`/admin/export/search-history?${params}`, { responseType:'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href=url; a.download='search_history.csv'; a.click();
      toast.success('Exported');
    } catch { toast.error('Export failed'); }
  };

  const totalPages = Math.ceil(total/PER);

  return (
    <div>
      <div className="admin-page-title">Search History</div>
      <div className="admin-page-sub">All user searches — {total.toLocaleString()} total records</div>

      <div className="toolbar mb-16">
        <div className="toolbar-left">
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            <Filter size={13} style={{color:'var(--text3)'}}/>
            <select className="select" style={{width:220}} value={userF} onChange={e=>setUserF(e.target.value)}>
              <option value="">All Users</option>
              {users.map(u=><option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>)}
            </select>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            <Calendar size={13} style={{color:'var(--text3)'}}/>
            <input className="input" type="date" value={fromF} onChange={e=>setFromF(e.target.value)} style={{width:140}} placeholder="From"/>
            <span style={{color:'var(--text3)',fontSize:12}}>to</span>
            <input className="input" type="date" value={toF} onChange={e=>setToF(e.target.value)} style={{width:140}} placeholder="To"/>
          </div>
          {(userF||fromF||toF) && <button className="btn btn-ghost btn-sm" onClick={()=>{setUserF('');setFromF('');setToF('');}}>Clear filters</button>}
        </div>
        <div className="toolbar-right">
          <button className="btn btn-success btn-sm" onClick={exportCSV}><Download size={13}/> Export CSV</button>
        </div>
      </div>

      {loading ? <div className="empty-state"><div className="spinner spinner-lg"/></div> : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>User</th><th>Company</th><th>Location</th><th>Radius</th><th>Business Types</th><th>Keyword</th><th>Min Rating</th><th>Results</th><th>Duration</th><th>Searched At</th></tr>
              </thead>
              <tbody>
                {history.map(h => {
                  const types = typeof h.business_types === 'string' ? JSON.parse(h.business_types||'[]') : h.business_types||[];
                  return (
                    <tr key={h.id}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:26, height:26, borderRadius:'50%', background:'linear-gradient(135deg,var(--accent),var(--accent2))', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'#fff', flexShrink:0 }}>
                            {h.first_name?.[0]}{h.last_name?.[0]}
                          </div>
                          <div>
                            <div style={{ fontWeight:600, fontSize:13, color:'var(--text)' }}>{h.first_name} {h.last_name}</div>
                            <div style={{ fontSize:11, color:'var(--text3)' }}>{h.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{fontSize:12.5}}>{h.company||'—'}</td>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                          <MapPin size={11} style={{color:'var(--accent)',flexShrink:0}}/>
                          <span style={{ fontWeight:600, fontSize:13, color:'var(--text)', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{h.location}</span>
                        </div>
                      </td>
                      <td><span className="badge badge-accent">{h.radius>=1000?`${h.radius/1000}km`:`${h.radius}m`}</span></td>
                      <td>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:3, maxWidth:180 }}>
                          {types.slice(0,3).map(t=>(
                            <span key={t} style={{ background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:4, padding:'1px 6px', fontSize:10, color:'var(--text3)', textTransform:'capitalize' }}>{t.replace(/_/g,' ')}</span>
                          ))}
                          {types.length > 3 && <span style={{ fontSize:10.5, color:'var(--text3)' }}>+{types.length-3}</span>}
                          {types.length === 0 && <span style={{fontSize:12,color:'var(--text3)'}}>All</span>}
                        </div>
                      </td>
                      <td style={{fontSize:12.5}}>{h.keyword||<span style={{color:'var(--text3)'}}>—</span>}</td>
                      <td>{h.min_rating>0?<span className="badge badge-gold">{h.min_rating}★+</span>:<span style={{color:'var(--text3)'}}>Any</span>}</td>
                      <td><span style={{ fontWeight:700, color:'var(--accent)', fontSize:14 }}>{(h.results_count||0).toLocaleString()}</span></td>
                      <td><span style={{fontSize:12,color:'var(--text3)'}}>{h.search_duration_ms?`${(h.search_duration_ms/1000).toFixed(1)}s`:'—'}</span></td>
                      <td style={{fontSize:11.5,color:'var(--text3)',whiteSpace:'nowrap'}}>{new Date(h.searched_at).toLocaleString()}</td>
                    </tr>
                  );
                })}
                {!history.length && <tr><td colSpan={10} style={{textAlign:'center',padding:32,color:'var(--text3)'}}>No search history found</td></tr>}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="pagination mt-16">
              <button className="page-btn" onClick={()=>load(page-1)} disabled={page===1}>‹</button>
              <span style={{fontSize:13,color:'var(--text2)'}}>Page {page} of {totalPages} · {total.toLocaleString()} records</span>
              <button className="page-btn" onClick={()=>load(page+1)} disabled={page===totalPages}>›</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
